import { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { ReceivingTable } from './ReceivingTable';
import { ReceiveBatchForm } from './ReceiveBatchForm';
import type { ManufacturerGroup as ManufacturerGroupType } from '@/features/frames-to-order/services/framesService';

interface ManufacturerGroupReceivingProps {
  group: ManufacturerGroupType;
  selectedFrameIds: string[];
  onSelectionChange: (frameIds: string[]) => void;
  onReceiveSuccess: () => void;
}

export function ManufacturerGroupReceiving({
  group,
  selectedFrameIds,
  onSelectionChange,
  onReceiveSuccess,
}: ManufacturerGroupReceivingProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isReceiveFormOpen, setIsReceiveFormOpen] = useState(false);

  const { manufacturer, frames, frameCount } = group;

  // Get selected frames for this manufacturer only
  const selectedInGroup = frames.filter((f) => selectedFrameIds.includes(f.id));
  const selectedCount = selectedInGroup.length;

  const handleSelectAll = () => {
    const allFrameIds = frames.map((f) => f.id);
    if (selectedCount === frameCount) {
      // Deselect all in this group
      const remaining = selectedFrameIds.filter((id) => !allFrameIds.includes(id));
      onSelectionChange(remaining);
    } else {
      // Select all in this group
      const allIds = [...new Set([...selectedFrameIds, ...allFrameIds])];
      onSelectionChange(allIds);
    }
  };

  const handleReceiveSelected = () => {
    if (selectedCount === 0) return;
    setIsReceiveFormOpen(true);
  };

  const handleReceiveAll = () => {
    // Select all frames in this group first
    const allFrameIds = frames.map((f) => f.id);
    const allIds = [...new Set([...selectedFrameIds, ...allFrameIds])];
    onSelectionChange(allIds);
    // Then open the form
    setIsReceiveFormOpen(true);
  };

  const handleReceiveSuccess = () => {
    // Clear selection for this manufacturer
    const frameIdsInGroup = frames.map((f) => f.id);
    const remaining = selectedFrameIds.filter((id) => !frameIdsInGroup.includes(id));
    onSelectionChange(remaining);
    onReceiveSuccess();
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full border-2 border-gray-300 text-gray-600 font-semibold">
              {isExpanded ? '−' : '+'}
            </div>
            <div className="text-left">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="text-xl font-bold text-gray-900">{manufacturer.name}</h3>
              </div>
              <p className="text-sm text-gray-600">
                {frameCount} frame{frameCount !== 1 ? 's' : ''} to receive
                {selectedCount > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    ({selectedCount} selected)
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {isExpanded ? 'Click to collapse' : 'Click to expand'}
          </div>
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-6">
          {/* Action Buttons */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedCount === frameCount ? 'Deselect All' : 'Select All'}
            </Button>

            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleReceiveSelected}
                disabled={selectedCount === 0}
              >
                Mark Selected as Received ({selectedCount})
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleReceiveAll}
              >
                Mark All as Received ({frameCount})
              </Button>
            </div>
          </div>

          {/* Frames Table */}
          <ReceivingTable
            frames={frames}
            selectedFrameIds={selectedFrameIds}
            onSelectionChange={onSelectionChange}
          />
        </div>
      )}

      {/* Receive Batch Form Modal */}
      <ReceiveBatchForm
        isOpen={isReceiveFormOpen}
        onClose={() => setIsReceiveFormOpen(false)}
        frames={selectedInGroup}
        onSuccess={handleReceiveSuccess}
      />
    </div>
  );
}
